import React, { useState, useEffect } from 'react';
import GameVsAI from './pages/GameVsAI.js';
import Login from './pages/Login.js';
import CreateAccount from './pages/CreateAccount.js';
import StatsScreen from './pages/StatsScreen.js';
import { DIFFICULTIES } from './utils/constants.js';
import { supabase } from './lib/supabase.js';

export default function App() {
  const [screen, setScreen] = useState('home');
  const [difficulty, setDifficulty] = useState(null);
  const [showDifficultyPicker, setShowDifficultyPicker] = useState(false);
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) loadUsername(session.user.id);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) loadUsername(session.user.id);
    });
    return () => subscription.unsubscribe();
  }, []);

  async function loadUsername(userId) {
    try {
      const { data } = await supabase.from('profiles').select('username').eq('id', userId).single();
      if (data) setUsername(data.username);
    } catch(e) {}
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    setUser(null);
    setUsername('');
    setScreen('home');
  }

  if (screen === 'login') return <Login onBack={() => setScreen('home')} onSuccess={() => setScreen('home')} onGoToRegister={() => setScreen('register')} />;
  if (screen === 'register') return <CreateAccount onBack={() => setScreen('home')} onSuccess={() => setScreen('login')} onGoToLogin={() => setScreen('login')} />;
  if (screen === 'stats') return <StatsScreen userId={user?.id} username={username} onBack={() => setScreen('home')} />;
  if (screen === 'game' && difficulty) return <GameVsAI onBack={() => { setScreen('home'); setDifficulty(null); }} difficulty={difficulty} playerName={username || 'Player'} userId={user?.id} />;

  const isMobile = window.innerWidth < 600;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#0d1b0f', backgroundImage: 'radial-gradient(ellipse at 50% 30%, rgba(45,106,79,0.4) 0%, transparent 60%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '1rem' : '2rem', fontFamily: "Lato, sans-serif", boxSizing: 'border-box', overflowX: 'hidden' }}>

      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, transparent, #c9a84c, #f0c040, #c9a84c, transparent)' }} />

      {user && (
        <div style={{ position: 'fixed', top: '8px', right: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', zIndex: 10 }}>
          <span style={{ color: '#c9a84c', fontSize: isMobile ? '0.6rem' : '0.75rem', maxWidth: isMobile ? '100px' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>👤 {username || user.email}</span>
          <button onClick={() => setScreen('stats')} style={{ background: 'transparent', color: 'rgba(201,168,76,0.5)', border: '1px solid rgba(201,168,76,0.3)', padding: isMobile ? '3px 6px' : '4px 10px', fontSize: isMobile ? '0.6rem' : '0.7rem', cursor: 'pointer', borderRadius: '2px', textTransform: 'uppercase' }}>Stats</button>
          <button onClick={handleSignOut} style={{ background: 'transparent', color: 'rgba(201,168,76,0.5)', border: '1px solid rgba(201,168,76,0.3)', padding: isMobile ? '3px 6px' : '4px 10px', fontSize: isMobile ? '0.6rem' : '0.7rem', cursor: 'pointer', borderRadius: '2px', textTransform: 'uppercase' }}>Sign Out</button>
        </div>
      )}

      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: isMobile ? '1.5rem' : '2.5rem', width: '100%' }}>
        <div style={{ fontSize: isMobile ? '2.5rem' : '4rem', color: '#c9a84c', lineHeight: 1, marginBottom: '0.25rem' }}>♠</div>
        <h1 style={{ fontFamily: "Playfair Display, serif", fontSize: isMobile ? '3rem' : '5rem', fontWeight: '900', color: '#c9a84c', letterSpacing: isMobile ? '0.15em' : '0.3em', textTransform: 'uppercase', margin: '0', lineHeight: 1, textShadow: '0 0 30px rgba(201,168,76,0.3)' }}>Pounce</h1>
        <h2 style={{ fontFamily: "Playfair Display, serif", fontSize: isMobile ? '1.2rem' : '2rem', fontWeight: '400', color: '#f5e6c8', letterSpacing: isMobile ? '0.4em' : '0.8em', textTransform: 'uppercase', margin: '0', lineHeight: 1 }}>League</h2>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', margin: '1rem 0 0.25rem' }}>
          <div style={{ height: '1px', width: '60px', background: 'linear-gradient(90deg, transparent, #c9a84c)' }} />
          <span style={{ color: '#c9a84c' }}>♦</span>
          <div style={{ height: '1px', width: '60px', background: 'linear-gradient(90deg, #c9a84c, transparent)' }} />
        </div>
        <p style={{ color: '#c9a84c', fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', opacity: 0.7, margin: 0 }}>Est. 2024 — Las Vegas, NV</p>
      </div>

      {!isMobile && <div style={{ fontSize: '4rem', marginBottom: '2rem', opacity: 0.15, letterSpacing: '0.5rem' }}>🎩🥃🎶</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%', maxWidth: '340px' }}>
        {!showDifficultyPicker ? (
          <button style={{ background: 'linear-gradient(135deg, #c9a84c, #f0c040, #c9a84c)', color: '#0d1b0f', border: 'none', padding: '1rem 2rem', fontSize: '1rem', fontWeight: '700', fontFamily: "Playfair Display, serif", letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: '2px', boxShadow: '0 4px 20px rgba(201,168,76,0.4)' }} onClick={() => setShowDifficultyPicker(true)}>
            ♠ Play vs AI
          </button>
        ) : (
          <div style={{ border: '1px solid rgba(201,168,76,0.3)', borderRadius: '4px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <p style={{ color: '#c9a84c', fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase', textAlign: 'center', margin: '0 0 0.25rem 0', fontFamily: "Playfair Display, serif" }}>♠ Choose Your Opponent</p>
            {Object.entries(DIFFICULTIES).map(([key, diff]) => (
              <button key={key} style={{ background: 'rgba(0,0,0,0.3)', color: diff.color, border: "1px solid " + diff.color + "40", padding: '0.85rem 1rem', fontSize: '0.85rem', fontFamily: "Playfair Display, serif", letterSpacing: '0.05em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: '2px', textAlign: 'left' }}
                onClick={() => { setDifficulty(key); setScreen('game'); setShowDifficultyPicker(false); }}>
                <div style={{ fontWeight: '700' }}>{diff.name}</div>
                <div style={{ fontSize: '0.7rem', opacity: 0.7, marginTop: '2px', textTransform: 'none' }}>{diff.description}</div>
              </button>
            ))}
            <button style={{ background: 'transparent', color: 'rgba(201,168,76,0.4)', border: 'none', fontSize: '0.75rem', cursor: 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '0.25rem' }} onClick={() => setShowDifficultyPicker(false)}>← Cancel</button>
          </div>
        )}

        <button style={{ background: 'transparent', color: '#c9a84c', border: '1px solid #c9a84c', padding: '1rem 2rem', fontSize: '1rem', fontWeight: '700', fontFamily: "Playfair Display, serif", letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: '2px' }}>♣ Play Online</button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '0.25rem 0' }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(201,168,76,0.2)' }} />
          <span style={{ color: 'rgba(201,168,76,0.4)', fontSize: '0.7rem', letterSpacing: '0.1em' }}>OR</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(201,168,76,0.2)' }} />
        </div>

        {!user ? (
          <React.Fragment>
            <button style={{ background: 'transparent', color: '#f5e6c8', border: '1px solid rgba(245,230,200,0.3)', padding: '0.75rem 2rem', fontSize: '0.85rem', fontFamily: "Lato, sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: '2px' }} onClick={() => setScreen('login')}>Sign In</button>
            <button style={{ background: 'transparent', color: '#f5e6c8', border: '1px solid rgba(245,230,200,0.3)', padding: '0.75rem 2rem', fontSize: '0.85rem', fontFamily: "Lato, sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: '2px' }} onClick={() => setScreen('register')}>Create Account</button>
          </React.Fragment>
        ) : (
          <div style={{ textAlign: 'center', color: 'rgba(201,168,76,0.6)', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            ✓ Signed in as {username || user.email}
          </div>
        )}
      </div>

      <p style={{ position: 'fixed', bottom: '1rem', color: 'rgba(201,168,76,0.3)', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Pounce League © 2024</p>
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, transparent, #c9a84c, #f0c040, #c9a84c, transparent)' }} />
    </div>
  );
}
